import React, { useEffect, useMemo, useState } from "react";
import { breakpoints, useMediaQuery } from "@tvg/design-system";
import { useSelector } from "react-redux";
import { get } from "lodash";
import useTalentPicks from "@urp/talent-picks/src/hooks/useTalentPicks";
import useWagerProfile from "@tvg/custom-hooks/hooks/useWagerProfile";
import { RaceProgram, RaceStatusEnum } from "@tvg/ts-types/Race";
import { getBettingInfo } from "@tvg/desktop-bet/src/utils/betUtils";
import { Props } from "./types";
import { Container, RaceInfoContainer } from "./styled-components";
import Header from "./Header";
import { getHandicappingModule } from "../../../store/selectors";
import { handicappingTabEvent } from "../../../utils/gtm/navigation";
import { HandicappingTabValue, HandicappingTab } from "../../../types";
import Comments from "./Comments";
import {
  getFilteredTabs,
  getRaceComments,
  getSortedRunners
} from "./functions";
import Runners from "./Runners";
import { talentPickTab } from "./contants";
import TalentPicks from "./TalentPicks";
import {
  handicappingDefaultTabs,
  handicappingDefaultTitle
} from "../../../store/defaultValues";

const Handicapping = ({
  race,
  runners,
  wagerType,
  selections,
  onClickBetButton,
  isWagerable,
  isBetSlipVisible,
  numRunnersAllRaces,
  legPosition,
  isScrollableRacecard,
  isLogged,
  races
}: Props) => {
  const wagerProfile = useWagerProfile();
  const { talentPicks } = useTalentPicks({
    raceNumber: race.raceNumber,
    trackCode: race.track?.trackCode,
    wagerProfile,
    races
  });

  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);

  // SELECTORS
  const { title = handicappingDefaultTitle, tabs = handicappingDefaultTabs } =
    useSelector(getHandicappingModule);

  // VARIABLES
  const raceComment = race.timeform?.analystVerdict || "";
  const trackDatasource = race.track?.trackDataSource || "";
  const numOfPicks = talentPicks.talentPicks.length;
  const isRaceOff = race.status?.code === RaceStatusEnum.RACE_OFF;
  const isResulted = race.results?.runners || race.results?.payoffs;
  const [allTabs, setAllTabs] = useState<HandicappingTab[]>([]);
  const [allTabsDefaultPosition, setAllTabsDefaultPosition] = useState(0);

  const filteredTabs = useMemo(
    () =>
      getFilteredTabs(
        tabs,
        trackDatasource,
        handicappingDefaultTabs.USGSA,
        runners,
        raceComment || ""
      ),
    [runners, raceComment]
  );

  useEffect(() => {
    const newTabsArray: HandicappingTab[] = [...filteredTabs];
    let selectedTab = 0;

    if (numOfPicks > 0) {
      newTabsArray.push(talentPickTab);
      selectedTab = newTabsArray.length - 1;
    }

    setAllTabsDefaultPosition(selectedTab);
    setAllTabs(newTabsArray);
  }, [numOfPicks, talentPicks]);

  const activeDefaultTab = get(allTabs, allTabsDefaultPosition);

  // STATE
  const [activeDatasource, setActiveDatasource] = useState(
    activeDefaultTab?.value
  );
  const [activeSourceName, setActiveSourceName] = useState(
    activeDefaultTab?.name
  );

  useEffect(() => {
    setActiveDatasource(activeDefaultTab?.value);
    setActiveSourceName(activeDefaultTab?.name);
  }, [activeDefaultTab]);

  const sortedRunners = getSortedRunners(runners, activeDatasource);

  const onChange = (value: string) => {
    setActiveDatasource(value as HandicappingTabValue);

    const selectedTab = allTabs.find((tab) => tab.value === value);

    if (selectedTab) {
      setActiveSourceName(selectedTab.name);
      handicappingTabEvent({
        label: selectedTab.name,
        trackName: race.track?.trackName,
        raceNumber: race.raceNumber
      });
    }
  };

  const runnerComments = useMemo(
    () => getRaceComments(sortedRunners, activeDatasource),
    [sortedRunners, activeDatasource]
  );

  useEffect(() => {
    if (
      numOfPicks > 0 &&
      allTabs.length === 1 &&
      activeDatasource !== "talentPick"
    ) {
      setActiveDatasource("talentPick");
    }
  }, [numOfPicks, allTabs, activeDatasource]);

  const { numberOfWagerableRunners } = getBettingInfo(
    selections,
    0,
    race,
    // We only need to retrieve the "numberOfWagerableRunners"
    // so we are only providing the "numRunners" property instead of the whole "races" object
    numRunnersAllRaces as RaceProgram[],
    wagerType
  );

  const availableTabs = !isLogged
    ? allTabs.filter((tab) => tab.value.includes("talentPick"))
    : allTabs;

  const hasTalentPicksOnHandicapping = availableTabs.some((tab) =>
    tab.value.includes("talentPick")
  );

  return (
    <>
      {(isLogged || hasTalentPicksOnHandicapping) && (
        <Container>
          <Header
            title={title}
            tabs={availableTabs}
            onChange={onChange}
            activeDataSource={activeDatasource}
            talentPicksCount={numOfPicks}
            isResulted={isResulted}
            isRaceOff={isRaceOff}
            isMobile={isMobile}
          />
          <RaceInfoContainer>
            {activeDatasource === "talentPick" ? (
              <TalentPicks
                race={race}
                talentPicks={talentPicks.talentPicks}
                isBetSlipVisible={isBetSlipVisible}
                numWagerableRunners={numberOfWagerableRunners}
              />
            ) : (
              isLogged && (
                <>
                  <Comments
                    raceComment={raceComment}
                    runnerComments={runnerComments}
                    isMobile={isMobile}
                  />
                  <Runners
                    isScrollableRacecard={isScrollableRacecard}
                    legPosition={legPosition}
                    runners={sortedRunners}
                    trackDataSource={trackDatasource}
                    activeDatasource={activeDatasource}
                    activeSourceName={activeSourceName}
                    wagerType={wagerType}
                    selections={selections}
                    onClickBetButton={onClickBetButton}
                    isWagerable={isWagerable}
                  />
                </>
              )
            )}
          </RaceInfoContainer>
        </Container>
      )}
    </>
  );
};

export default Handicapping;
